import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from '@services/auth/auth.service';

/**
 * Generic SSE client for streaming LLM endpoints.
 *
 * Backed by Fetch + ReadableStream (rather than EventSource) so we can attach
 * a Bearer token and PUT a JSON body. Each `data: <json>\n\n` line is parsed
 * and emitted as the next observable value, wrapped in NgZone so component
 * change detection fires.
 */
@Injectable({ providedIn: 'root' })
export class SseClientService {
    constructor(
        private authSvc: AuthService,
        private ngZone: NgZone,
    ) {}

    streamSSE<TEvent = unknown>(url: string, body: object): Observable<TEvent> {
        return new Observable<TEvent>((observer) => {
            const abortController = new AbortController();
            const token = this.authSvc.getAccessToken();

            fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
                signal: abortController.signal,
            })
                .then((response) => {
                    if (!response.body) {
                        throw new Error(`HTTP ${response.status}: response body is null`);
                    }
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let sawEvent = false;

                    const read = (): void => {
                        reader
                            .read()
                            .then(({ done, value }) => {
                                if (done) {
                                    if (!sawEvent && !response.ok) {
                                        // Non-200 with no parseable SSE
                                        // event: surface the status so the
                                        // caller can fall back to a generic
                                        // error message.
                                        this.ngZone.run(() => observer.error(new Error(`HTTP ${response.status}`)));
                                        return;
                                    }
                                    this.ngZone.run(() => observer.complete());
                                    return;
                                }
                                buffer += decoder.decode(value, { stream: true });
                                const lines = buffer.split('\n\n');
                                buffer = lines.pop() || '';

                                for (const line of lines) {
                                    if (line.startsWith('data: ')) {
                                        try {
                                            const event: TEvent = JSON.parse(line.slice(6));
                                            sawEvent = true;
                                            this.ngZone.run(() => observer.next(event));
                                        } catch {
                                            // Skip malformed events
                                        }
                                    }
                                }
                                read();
                            })
                            .catch((err) => {
                                if (err.name !== 'AbortError') {
                                    this.ngZone.run(() => observer.error(err));
                                }
                            });
                    };
                    read();
                })
                .catch((err) => {
                    this.ngZone.run(() => observer.error(err));
                });

            return () => {
                abortController.abort();
            };
        });
    }
}
